import type { AppLanguage } from '../i18n/types';

/** Public page (Google Play "Delete account URL"); generated by scripts/writePublicLegalPages.cjs. */
export const ACCOUNT_DELETION_PUBLIC_PATH = '/delete-account/';
export const ACCOUNT_DELETION_CONTACT_EMAIL = 'info@offizz.ai';
export const ACCOUNT_DELETION_GRACE_DAYS = 30;
export const ACCOUNT_DELETION_RETENTION_YEARS = 7;

export type AccountDeletionContent = {
  heading: string;
  intro: string;
  inAppTitle: string;
  inAppSteps: string[];
  emailTitle: string;
  emailSteps: string[];
  beforeTitle: string;
  before: string[];
  deletedTitle: string;
  deleted: string[];
  keptTitle: string;
  kept: string[];
  keptNote: string;
  ownersTitle: string;
  owners: string[];
};

const EN: AccountDeletionContent = {
  heading: 'Delete your PLACY account',
  intro:
    'PLACY is operated by A.Y. Offices and Real Estate Ltd. You can ask us to delete your PLACY account and the personal data associated with it at any time, from the app or by email.',
  inAppTitle: 'Delete from the app',
  inAppSteps: [
    'Open the PLACY app and sign in.',
    'Go to Profile.',
    'Tap “Delete account”, then “Delete my account”, and confirm.',
    'You are signed out immediately. Your account is deactivated at once and permanently deleted within 30 days.',
  ],
  emailTitle: 'Delete by email (if you no longer have the app)',
  emailSteps: [
    `Email ${ACCOUNT_DELETION_CONTACT_EMAIL} from the email address registered to your account.`,
    'Use the subject “Delete my account” and include the phone number on the account.',
    'We may ask you to confirm the request. We will confirm by email once the account is deleted, within 30 days.',
  ],
  beforeTitle: 'Before you delete',
  before: [
    'Upcoming bookings must be cancelled (under the asset’s cancellation policy) or completed before the account can be deleted.',
    'You can cancel the request within 30 days by emailing us. After that, deletion is permanent and cannot be undone.',
    'After deletion you can register again with the same email or phone number as a new account.',
  ],
  deletedTitle: 'Data we delete (within 30 days)',
  deleted: [
    'Profile details: name, profile photo, date of birth, ID number, and address.',
    'Login details: password, Google or Apple sign-in link, and active sessions.',
    'Email and phone number on your account (except where kept with booking records below).',
    'Favorites, recently viewed listings, and search and voice-assistant history.',
    'Location, device, and push-notification data.',
    'Saved payment cards (the stored card token is deleted at our payment provider).',
    'Health questionnaires (for example for gyms).',
    'Marketing preferences and mailing-list subscriptions.',
    'Messages that are not related to a booking.',
    'Reviews are anonymized and shown as “Former user”.',
  ],
  keptTitle: 'Data we keep, and for how long',
  kept: [
    'Booking records (booking number, listing, dates, price, status, cancellations): 7 years after the booking.',
    'Invoices, receipts, and payment transaction references: 7 years, as required by Israeli tax law.',
    'Your name, email, and phone number attached to those bookings only: 7 years, to handle disputes and chargebacks.',
    'Messages with an asset owner about a specific booking: 7 years, as evidence in disputes.',
    'Security and fraud-prevention logs: up to 12 months.',
  ],
  keptNote:
    'Kept data is stored in a restricted archive, is not used for marketing, and is deleted or anonymized when the retention period ends.',
  ownersTitle: 'Asset owners',
  owners: [
    'Your listings are unpublished immediately and deleted together with the account.',
    'Invoices, payouts, and commission records are kept for 7 years (tax law).',
    'Bank account details are deleted after the final payout.',
  ],
};

const HE: AccountDeletionContent = {
  heading: 'מחיקת חשבון PLACY',
  intro:
    'PLACY מופעלת על ידי א.י משרדים ונדל"ן בע"מ. ניתן לבקש בכל עת למחוק את חשבון PLACY ואת המידע האישי הקשור אליו, דרך האפליקציה או בדוא"ל.',
  inAppTitle: 'מחיקה דרך האפליקציה',
  inAppSteps: [
    'פותחים את אפליקציית PLACY ומתחברים.',
    'עוברים לפרופיל.',
    'לוחצים על "מחיקת חשבון", אחר כך על "מחק את החשבון שלי", ומאשרים.',
    'תנותקו מיד. החשבון מושבת מיד ונמחק לצמיתות תוך 30 יום.',
  ],
  emailTitle: 'מחיקה בדוא"ל (אם האפליקציה כבר לא מותקנת)',
  emailSteps: [
    `שולחים דוא"ל אל ${ACCOUNT_DELETION_CONTACT_EMAIL} מכתובת הדוא"ל הרשומה בחשבון.`,
    'בנושא ההודעה כותבים "מחיקת חשבון" ומציינים את מספר הטלפון שבחשבון.',
    'ייתכן שנבקש לאשר את הבקשה. נאשר בדוא"ל כשהחשבון נמחק, תוך 30 יום.',
  ],
  beforeTitle: 'לפני המחיקה',
  before: [
    'יש לבטל הזמנות עתידיות (לפי מדיניות הביטול של הנכס) או להמתין לסיומן לפני שניתן למחוק את החשבון.',
    'ניתן לבטל את הבקשה תוך 30 יום בפנייה אלינו בדוא"ל. לאחר מכן המחיקה סופית ואינה ניתנת לשחזור.',
    'לאחר המחיקה ניתן להירשם מחדש עם אותו דוא"ל או טלפון כחשבון חדש.',
  ],
  deletedTitle: 'מידע שנמחק (תוך 30 יום)',
  deleted: [
    'פרטי פרופיל: שם, תמונת פרופיל, תאריך לידה, מספר תעודת זהות וכתובת.',
    'פרטי התחברות: סיסמה, קישור התחברות עם Google או Apple, וחיבורים פעילים.',
    'הדוא"ל והטלפון שבחשבון (למעט מה שנשמר עם רשומות ההזמנות כמפורט בהמשך).',
    'מועדפים, נכסים שנצפו לאחרונה, והיסטוריית חיפוש ועוזר קולי.',
    'נתוני מיקום, מכשיר והתראות.',
    'כרטיסי אשראי שמורים (האסימון השמור נמחק אצל ספק התשלומים).',
    'שאלוני בריאות (למשל לחדרי כושר).',
    'העדפות שיווק והרשמה לרשימות תפוצה.',
    'הודעות שאינן קשורות להזמנה.',
    'ביקורות הופכות לאנונימיות ומוצגות בשם "משתמש לשעבר".',
  ],
  keptTitle: 'מידע שנשמר, ולכמה זמן',
  kept: [
    'רשומות הזמנה (מספר הזמנה, נכס, תאריכים, מחיר, סטטוס, ביטולים): 7 שנים ממועד ההזמנה.',
    'חשבוניות, קבלות ואסמכתאות תשלום: 7 שנים, כנדרש בדיני המס בישראל.',
    'שם, דוא"ל וטלפון המשויכים להזמנות אלה בלבד: 7 שנים, לטיפול במחלוקות ובהכחשות עסקה.',
    'הודעות עם בעל נכס בנוגע להזמנה מסוימת: 7 שנים, כראיה במחלוקות.',
    'יומני אבטחה ומניעת הונאות: עד 12 חודשים.',
  ],
  keptNote:
    'המידע הנשמר מאוחסן בארכיון מוגבל גישה, אינו משמש לשיווק, ונמחק או הופך לאנונימי בתום תקופת השמירה.',
  ownersTitle: 'בעלי נכסים',
  owners: [
    'הנכסים שלכם מוסרים מהפרסום מיד ונמחקים יחד עם החשבון.',
    'חשבוניות, תשלומים ורשומות עמלה נשמרים 7 שנים (דיני מס).',
    'פרטי חשבון הבנק נמחקים לאחר התשלום האחרון.',
  ],
};

const BY_LANGUAGE: Record<AppLanguage, AccountDeletionContent> = { en: EN, he: HE };

export function getAccountDeletionContent(language: AppLanguage): AccountDeletionContent {
  return BY_LANGUAGE[language];
}
